import { useMemo, useEffect } from 'react'
import Row from '../../common/Row'
import useForceUpdate from 'hooks/useForceUpdate'
import VisitModalityIcon, { VISIT_MODALITY } from '../../common/VisitModalityIcon'
import AlarmClockIcon from '@ui-components-3/icons/lib/light/AlarmClock'
import LocationDotIcon from '@ui-components-3/icons/lib/solid/LocationDot'
import Pill from 'components/common/Pill'
import AslInterpreterPill from 'components/common/AslInterpreterPill'

const TOKEN_VALUES = [3600000, 60000, 1000]

const TOKEN_NAMES = ['h', 'm', 's']

const formatDuration = (date: Date) => {
  let diff = Date.now() - +date
  let str = ''

  for (let i = 0, tokenValue, wholeValue; i < TOKEN_VALUES.length; i++) {
    tokenValue = TOKEN_VALUES[i]
    wholeValue = Math.trunc(diff / tokenValue)
    if (wholeValue) {
      str += `${wholeValue}${TOKEN_NAMES[i]} `
      diff -= wholeValue * tokenValue
    }
  }

  return str && str.slice(0, -1)
}

type TimerProps = {
  date: string
}

const Timer = ({ date }: TimerProps) => {
  const forceUpdate = useForceUpdate()

  const dateObject = useMemo(() => new Date(date), [date])

  useEffect(() => {
    const handle = setInterval(forceUpdate, 1000)
    return () => clearInterval(handle)
  }, [])

  return formatDuration(dateObject)
}

type ContentProps = {
  name: string
  age: number
  state: string
  modality: VISIT_MODALITY
  shouldHideClock: boolean
  waitTime: string
  wasSentToReview: boolean
  address?: string
  isSuccessVisit?: boolean
  showAslPill?: boolean
}

const Content = ({
  name,
  age,
  address,
  state,
  modality,
  waitTime,
  wasSentToReview,
  shouldHideClock,
  isSuccessVisit,
  showAslPill,
}: ContentProps) => {
  return (
    <>
      <Row>
        <VisitModalityIcon modality={modality} />
        <h5 className="typography-body-xxl text-primary-800 tracking-tight">
          {name}, {age}
        </h5>
      </Row>
      <div className="flex justify-between gap-x-4 gap-y-2 flex-wrap">
        <div className="shrink-0">
          {state ? (
            <Row grey>
              <LocationDotIcon className="w-5 h-5 !fill-neutral-300" aria-hidden="true" />
              <p className="typography-body-l">{state}</p>
            </Row>
          ) : null}
          {address ? (
            <Row grey>
              <LocationDotIcon className="w-5 h-5 !fill-neutral-300" aria-hidden="true" />
              <p className="typography-body-l">{address}</p>
            </Row>
          ) : null}
        </div>
        {(!!isSuccessVisit || !!showAslPill) && (
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {!!isSuccessVisit && <Pill label="Student Success" />}
            {!!showAslPill && <AslInterpreterPill />}
          </div>
        )}
      </div>

      <hr className="my-4" />
      {shouldHideClock ? null : (
        <Row>
          <AlarmClockIcon className="h-5 w-5" aria-hidden="true" />
          <p className="typography-body-l text-neutral-700">
            {wasSentToReview ? 'In review: ' : 'Wait time: '}
            <Timer date={waitTime} />
          </p>
        </Row>
      )}
    </>
  )
}

export default Content
